import React, { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { useConfirm } from "./ConfirmProvider";
import {
  Send, Loader2, X, Paperclip, Image as ImageIcon, MessageCircle,
  Pencil, Trash2, Check, MoreHorizontal,
} from "lucide-react";

/**
 * Shared "Updates" feed used by both the ID firm's Project Hub and the
 * homeowner's project view. Supports photo uploads, per-post comments,
 * and edit/delete by the original author.
 */
export default function ProjectFeed({ projectId, canPost = true, onCounterChange }) {
  const { user } = useAuth();
  const [feed, setFeed] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/projects/${projectId}/feed`);
      setFeed(data.feed || []);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Mark this user as caught-up when the feed mounts so the unread pill clears.
    api.post(`/projects/${projectId}/feed/mark-read`).catch(() => {});
    onCounterChange && onCounterChange(0);
  }, [projectId]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-6" data-testid="project-feed">
      {canPost && (
        <PostComposer projectId={projectId} onPosted={load} />
      )}

      {loading && feed.length === 0 && (
        <div className="card-soft text-center text-[#7A7571] py-6">
          <Loader2 size={14} className="animate-spin inline" /> Loading updates…
        </div>
      )}
      {!loading && feed.length === 0 && (
        <div className="card-soft text-center text-[#7A7571] py-8">No updates yet.</div>
      )}
      {feed.map((p) => (
        <FeedPostCard key={p.post_id} post={p} currentUser={user} onReload={load} />
      ))}
    </div>
  );
}

function PostComposer({ projectId, onPosted }) {
  const [content, setContent] = useState("");
  const [files, setFiles] = useState([]);
  const [posting, setPosting] = useState(false);
  const inputRef = useRef(null);

  const pickFiles = (e) => {
    const list = Array.from(e.target.files || []);
    if (list.length + files.length > 8) {
      toast.error("Max 8 photos per update");
      return;
    }
    setFiles((prev) => [...prev, ...list]);
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!content.trim() && files.length === 0) {
      toast.error("Add a note or attach a photo");
      return;
    }
    setPosting(true);
    try {
      const fd = new FormData();
      fd.append("content", content.trim());
      files.forEach((f) => fd.append("files", f));
      await api.post(`/projects/${projectId}/feed`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Update posted");
      setContent("");
      setFiles([]);
      if (inputRef.current) inputRef.current.value = "";
      onPosted && (await onPosted());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPosting(false);
    }
  };

  return (
    <form onSubmit={submit} className="card-soft space-y-3" data-testid="feed-composer">
      <div className="text-xs uppercase tracking-widest font-bold text-[#7A7571]">Post an update</div>
      <textarea
        className="input-soft min-h-[80px]"
        placeholder="Share progress, questions, or photos here…"
        value={content}
        onChange={(e) => setContent(e.target.value)}
        data-testid="feed-content"
      />
      {files.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {files.map((f, i) => (
            <div key={i} className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#F7F4EC] text-xs text-[#2C2A29]">
              <ImageIcon size={11} />
              <span className="truncate max-w-[160px]">{f.name}</span>
              <button
                type="button"
                onClick={() => setFiles(files.filter((_, j) => j !== i))}
                className="text-[#9F1239] hover:opacity-80"
              >
                <X size={11} />
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="flex items-center justify-between">
        <label className="btn-ghost text-xs inline-flex items-center gap-1 cursor-pointer">
          <Paperclip size={12} /> Attach photo{files.length > 0 ? ` (${files.length})` : ""}
          <input ref={inputRef} type="file" accept="image/*" multiple className="hidden" onChange={pickFiles} data-testid="feed-photo-input" />
        </label>
        <button
          type="submit"
          disabled={posting || (!content.trim() && files.length === 0)}
          data-testid="feed-submit"
          className="btn-primary inline-flex items-center gap-1.5 disabled:opacity-60"
        >
          {posting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
          {posting ? "Posting…" : "Post update"}
        </button>
      </div>
    </form>
  );
}

function FeedPostCard({ post, currentUser, onReload }) {
  const confirm = useConfirm();
  const isAuthor = post.author_id === currentUser?.user_id || post.created_by === currentUser?.user_id;
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState(post.content || "");
  const [savingEdit, setSavingEdit] = useState(false);
  const [showComments, setShowComments] = useState(false);

  const roleLabel = (() => {
    const r = post.author_role;
    if (r === "customer") return "Homeowner";
    if (r === "admin" || r === "pm" || r === "designer") return "ID firm";
    return null;
  })();

  const saveEdit = async () => {
    if (!editContent.trim()) { toast.error("Update can't be empty"); return; }
    setSavingEdit(true);
    try {
      await api.patch(`/feed/${post.post_id}`, { content: editContent.trim() });
      toast.success("Updated");
      setEditing(false);
      onReload && (await onReload());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingEdit(false);
    }
  };

  const doDelete = async () => {
    const ok = await confirm({
      title: "Delete this update?",
      message: "The post, its photos, and all comments will be removed.",
      confirmText: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/feed/${post.post_id}`);
      toast.success("Deleted");
      onReload && (await onReload());
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div className="card-soft" data-testid={`post-${post.post_id}`}>
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          {post.type === "milestone" && (
            <div className="text-[10px] uppercase tracking-widest font-bold text-[#0F766E] mb-1">Milestone reached</div>
          )}
          <div className="flex items-center flex-wrap gap-1.5 text-xs text-[#7A7571]">
            <span className="font-semibold text-[#2C2A29]">{post.author_name || "Team"}</span>
            {roleLabel && (
              <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                post.author_role === "customer"
                  ? "bg-[#FFEDD5] text-[#9A3412] border border-[#FDBA74]"
                  : "bg-[#E0F2FE] text-[#075985] border border-[#7DD3FC]"
              }`}>
                {roleLabel}
              </span>
            )}
            <span>· {new Date(post.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
            {post.edited_at && <span className="italic">· edited</span>}
          </div>
        </div>
        {isAuthor && !editing && (
          <PostActionsMenu onEdit={() => { setEditContent(post.content || ""); setEditing(true); }} onDelete={doDelete} postId={post.post_id} />
        )}
      </div>

      {editing ? (
        <div className="space-y-2">
          <textarea
            className="input-soft min-h-[80px]"
            value={editContent}
            onChange={(e) => setEditContent(e.target.value)}
            data-testid={`post-edit-${post.post_id}`}
          />
          <div className="flex items-center justify-end gap-2">
            <button type="button" className="btn-ghost text-sm" onClick={() => setEditing(false)}>Cancel</button>
            <button
              type="button"
              className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
              disabled={savingEdit}
              onClick={saveEdit}
              data-testid={`post-edit-save-${post.post_id}`}
            >
              {savingEdit ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
              Save
            </button>
          </div>
        </div>
      ) : (
        <>
          {post.content && (
            <p className="text-[#2C2A29] whitespace-pre-wrap leading-relaxed">{post.content}</p>
          )}
          {/* Legacy URL-based photo */}
          {post.photo_url && (
            <img src={post.photo_url} alt="" className="w-full rounded-2xl mt-3 max-h-72 object-cover" />
          )}
          {/* Uploaded images */}
          {(post.images || []).length > 0 && (
            <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
              {post.images.map((img) => {
                const src = `${api.defaults.baseURL}/feed/${post.post_id}/images/${img.image_id}`;
                return (
                  <a key={img.image_id} href={src} target="_blank" rel="noreferrer" className="aspect-square rounded-xl overflow-hidden border border-[#EBE5D9] hover:opacity-90">
                    <img src={src} alt="" className="w-full h-full object-cover" />
                  </a>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Comments */}
      <PostComments
        post={post}
        currentUser={currentUser}
        onReload={onReload}
        expanded={showComments}
        onToggle={() => setShowComments((v) => !v)}
      />
    </div>
  );
}

function PostActionsMenu({ onEdit, onDelete, postId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="p-1 rounded-full hover:bg-[#F7F4EC] text-[#7A7571]"
        aria-label="Post actions"
        data-testid={`post-menu-${postId}`}
      >
        <MoreHorizontal size={16} />
      </button>
      {open && (
        <>
          <button
            type="button"
            className="fixed inset-0 z-10"
            onClick={() => setOpen(false)}
            aria-label="Close menu"
          />
          <div className="absolute right-0 top-full mt-1 w-40 bg-white border border-[#EBE5D9] rounded-xl shadow-lg z-20 overflow-hidden">
            <button
              type="button"
              onClick={() => { setOpen(false); onEdit(); }}
              className="w-full px-3 py-2 text-sm text-left hover:bg-[#F7F4EC] flex items-center gap-2"
              data-testid={`post-edit-open-${postId}`}
            >
              <Pencil size={13} /> Edit
            </button>
            <button
              type="button"
              onClick={() => { setOpen(false); onDelete(); }}
              className="w-full px-3 py-2 text-sm text-left hover:bg-[#FEE2E2] text-[#B91C1C] flex items-center gap-2"
              data-testid={`post-delete-${postId}`}
            >
              <Trash2 size={13} /> Delete
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function PostComments({ post, currentUser, onReload, expanded, onToggle }) {
  const confirm = useConfirm();
  const comments = post.comments || [];
  const [newComment, setNewComment] = useState("");
  const [posting, setPosting] = useState(false);

  const submit = async () => {
    if (!newComment.trim()) return;
    setPosting(true);
    try {
      await api.post("/comments", {
        entity_type: "feed",
        entity_id: post.post_id,
        content: newComment.trim(),
      });
      setNewComment("");
      onReload && (await onReload());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setPosting(false);
    }
  };

  return (
    <div className="mt-3 pt-3 border-t border-[#EBE5D9]">
      <button
        type="button"
        onClick={onToggle}
        className="text-xs text-[#7A7571] hover:text-[#2C2A29] inline-flex items-center gap-1.5"
        data-testid={`post-comments-toggle-${post.post_id}`}
      >
        <MessageCircle size={13} />
        {comments.length === 0
          ? "Add a comment"
          : `${comments.length} comment${comments.length === 1 ? "" : "s"}`}
      </button>
      {expanded && (
        <div className="mt-2 space-y-2">
          {comments.map((c) => (
            <CommentRow
              key={c.comment_id}
              comment={c}
              currentUser={currentUser}
              onReload={onReload}
              confirm={confirm}
            />
          ))}
          <div className="flex items-start gap-2 pt-1">
            <input
              type="text"
              className="input-soft text-sm flex-1"
              placeholder="Write a reply…"
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); submit(); } }}
              data-testid={`comment-input-${post.post_id}`}
            />
            <button
              type="button"
              onClick={submit}
              disabled={posting || !newComment.trim()}
              data-testid={`comment-submit-${post.post_id}`}
              className="btn-primary text-sm inline-flex items-center gap-1.5 disabled:opacity-60"
            >
              {posting ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CommentRow({ comment, currentUser, onReload, confirm }) {
  const isAuthor = comment.author_id === currentUser?.user_id;
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(comment.content || "");
  const [saving, setSaving] = useState(false);

  const saveEdit = async () => {
    if (!text.trim()) { toast.error("Comment can't be empty"); return; }
    setSaving(true);
    try {
      await api.patch(`/comments/${comment.comment_id}`, { content: text.trim() });
      setEditing(false);
      onReload && (await onReload());
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    const ok = await confirm({
      title: "Delete this comment?",
      confirmText: "Delete",
      danger: true,
    });
    if (!ok) return;
    try {
      await api.delete(`/comments/${comment.comment_id}`);
      onReload && (await onReload());
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const roleLabel = comment.author_role === "customer" ? "Homeowner"
    : (["admin", "pm", "designer"].includes(comment.author_role) ? "ID firm" : null);

  return (
    <div className="rounded-xl bg-[#F7F4EC] p-2.5" data-testid={`comment-${comment.comment_id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="flex items-center flex-wrap gap-1.5 text-[11px] text-[#7A7571]">
            <span className="font-semibold text-[#2C2A29]">{comment.author_name || "Team"}</span>
            {roleLabel && (
              <span className={`px-1 py-0 rounded-full text-[9px] font-semibold uppercase tracking-wider ${
                comment.author_role === "customer"
                  ? "bg-[#FFEDD5] text-[#9A3412]"
                  : "bg-[#E0F2FE] text-[#075985]"
              }`}>
                {roleLabel}
              </span>
            )}
            <span>· {new Date(comment.created_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
            {comment.edited_at && <span className="italic">· edited</span>}
          </div>
          {editing ? (
            <div className="mt-1 flex items-center gap-2">
              <input
                type="text"
                className="input-soft text-sm flex-1"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEdit(); } }}
                autoFocus
              />
              <button type="button" onClick={saveEdit} disabled={saving} className="btn-primary text-xs px-2 py-1">
                {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
              </button>
              <button type="button" onClick={() => setEditing(false)} className="btn-ghost text-xs px-2 py-1">
                <X size={11} />
              </button>
            </div>
          ) : (
            <div className="text-sm text-[#2C2A29] whitespace-pre-wrap mt-1">{comment.content}</div>
          )}
        </div>
        {isAuthor && !editing && (
          <div className="flex items-center gap-0.5 flex-shrink-0">
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="p-1 rounded-full hover:bg-white/60 text-[#7A7571]"
              data-testid={`comment-edit-${comment.comment_id}`}
            >
              <Pencil size={11} />
            </button>
            <button
              type="button"
              onClick={doDelete}
              className="p-1 rounded-full hover:bg-[#FEE2E2] text-[#B91C1C]"
              data-testid={`comment-delete-${comment.comment_id}`}
            >
              <Trash2 size={11} />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
